import { SocialShare } from "@quintype/components";
import get from "lodash/get";
import throttle from "lodash/throttle";
import { array, bool, func, number, object, string } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import { HeroImage, SocialShareTemplate } from "../../../arrow";
import { CaptionAttribution } from "../../../arrow/components/Atoms/CaptionAttribution";
import { STATIC_KEYWORDS } from "../../../constants";
import { DfpComponent } from "../../ads/dfp-component";
import { BreadCrumb, Button, Headline, PublishDateAndTime, StoryAuthors, StoryAuthorImages } from "../../atoms";
import FortuneExchange from "../../atoms/FortuneExchange";
import LiveButton from "../../atoms/live-button";
import { useDeviceType } from "../../customHooks/useDeviceType";
import { getAiFaqStoryId, useAiFaqData } from "../../customHooks/useAiFaqData";
import { useSummaryCheck } from "../../customHooks/useSummaryCheck";
import { STORY_HEADER_IN_VIEW } from "../../store/actions";
import { MagazineStoryHeaderCard } from "../MagazineStoryHeaderCard";
import SummaryCard from "../SummaryCard";
import { TextWithReadMore } from "../../molecules/TextWithReadMore";
import { getAiSummaryEligibility, hasShowAiSummaryFalse, isSummaryBlockedByOwner } from "../../utils";
import { getCategoryAndSubCategory, getTemplateName } from "../../../ga-utils";
import AIHub from "../AIHub";

import "./StoryHeader.m.css";
import BookReviewCard from "../BookReviewCard";
import BookReviewButton from "../../atoms/BookReviewButton";
import MoviewReviewCard from "../MovieReviewCard";
import ReviewButton from "../../atoms/ReviewButton";

const OPEN_AI_HUB_EVENT = "open:open-aihub";
const FIRST_PRINCIPLES_TARGET = "first_principles";
const TIMELINE_TARGET = "timeline";
const STORY_HEADER_SWITCH_OFFSET = 75;
const STORY_HEADER_SELECTOR = "[data-story-header]";
const STORY_HOLDER_SELECTOR = ".story-holder";
const AI_HUB_STICKY_PARAGRAPH_TRIGGER_RATIO = 0.5;

const getAdStoryKey = (story = {}) =>
  (story["story-content-id"] || story["public-identifier"] || story["sequence-no"] || story.id || "story")
    .toString()
    .replace(/[^a-zA-Z0-9_-]/g, "-");

const StoryHeader = ({
  showAd,
  story,
  headline,
  storyAuthorData,
  publishTime,
  readTime,
  showHeroImage,
  setShowHighlights,
  magazineDetails,
  showReview,
  showBookReview,
  isValidBookReview = false,
  hideBreadcrumb = false,
  isContinueReadingPreviewActive = false,
  isPerpetualStory = false,
  storyInstanceId = "",
}) => {
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const storyHeaderRef = useRef(null);

  const qtStates = useSelector((state) => state);
  const qtState = get(qtStates, ["qt"], {});
  const qtConfig = get(qtState, ["config"], {});
  const networkId = get(qtState, ["config", "ads-config", "network_id"], {});
  const storyHeaderInView = get(qtStates, "storyHeaderInView", true);
  const storyHeaderInViewRef = useRef(storyHeaderInView);
  const storyTemplate = get(story, ["story-template"], "");
  const isLiveBlog = storyTemplate === "live-blog";
  const isMagazine = storyTemplate === "magazine";
  const isSponsoredStory = storyTemplate === "sponsored";
  const isTextStory = storyTemplate === "text";
  const isBookReview = storyTemplate === "book-review";
  const isMovieReview = storyTemplate === "review";
  const isSummaryEligibleStory = ["text", "magazine", "book-review", "review"].includes(storyTemplate);
  const { summaryPresent, showSummary, setShowSummary, summaryIndex } = useSummaryCheck(story.cards);
  const faqUrl = get(qtConfig, ["publisher-attributes", "publisher-faq-url"], false);
  const publisherAttributes = get(qtConfig, ["publisher-attributes"], {});
  const isInhouseAiSummaryEnabled =
    get(qtConfig, ["publisher-attributes", "publisher-inhouse-ai-summary"], false) === true;
  const isSummaryBlockedByStoryAttribute =
    isInhouseAiSummaryEnabled && (hasShowAiSummaryFalse(story) || isSummaryBlockedByOwner(story, publisherAttributes));
  const storyId = getAiFaqStoryId(story);
  const storyContentId = get(story, ["story-content-id"], "") || get(story, ["id"], "");
  const storyAdKey = getAdStoryKey(story);
  const { canUseApiSummaryFallback, shouldEnableAiHubForTextStory } = getAiSummaryEligibility({
    story,
    publisherConfig: publisherAttributes,
    summaryPresent,
  });
  const [aiHubClick, setAiHubClick] = useState(false);
  const [aiHubScrollTarget, setAiHubScrollTarget] = useState(null);
  const [showAiHubPanel, setShowAiHubPanel] = useState(false);
  const [isActivePerpetualStory, setIsActivePerpetualStory] = useState(!isPerpetualStory);
  const {
    hasSummary,
    hasFaqs,
    hasTopic,
    hasKeyTakeaways,
    hasTimeline,
    hasGlossary,
    hasGlossarySourcePhrases,
    hasTweets,
    hasInTheStory,
    summary,
    topic,
    keyTakeaways,
    timeline,
    faqs,
    glossary,
    tweets,
    inTheStory,
    headline: apiHeadline,
    heroImageS3Key: apiHeroImageS3Key,
    isLoading,
  } = useAiFaqData({
    faqUrl,
    storyId,
    enabled:
      (isSummaryEligibleStory && faqUrl !== false && canUseApiSummaryFallback && !isSummaryBlockedByStoryAttribute) ||
      shouldEnableAiHubForTextStory,
  });
  const hasApiSummary = hasSummary && Boolean(summary);
  const shouldUseApiSummary = canUseApiSummaryFallback && hasApiSummary;
  const shouldShowSummary = !isSummaryBlockedByStoryAttribute && (summaryPresent || shouldUseApiSummary);
  const isSummaryButtonPending = !summaryPresent && canUseApiSummaryFallback && isLoading;
  // const shouldRenderSummaryButton =
  //   ((!isSponsoredStory && storyTemplate === "text") || isBookReview || isMagazine || isMovieReview) &&
  //   !isSummaryBlockedByStoryAttribute &&
  //   (summaryPresent || canUseApiSummaryFallback);
  const shouldRenderSummaryButton =
    storyTemplate === "text" &&
    !shouldEnableAiHubForTextStory &&
    !isSummaryBlockedByStoryAttribute &&
    (summaryPresent || shouldUseApiSummary || isSummaryButtonPending);
  const isSummaryButtonEnabled = shouldShowSummary;
  const shouldShowAiHubButton =
    shouldEnableAiHubForTextStory &&
    !isLoading &&
    (hasSummary ||
      hasTopic ||
      hasFaqs ||
      hasKeyTakeaways ||
      hasTimeline ||
      hasGlossary ||
      hasGlossarySourcePhrases ||
      hasTweets ||
      hasInTheStory);
  const isAdfreePage = get(qtState, ["config", "subscriber"]) === "true";
  const hasMagazineIssueWidget = isMagazine && Boolean(magazineDetails);
  const fullWidth =
    (!showAd || isAdfreePage) && !isMagazine && !isBookReview && !hasMagazineIssueWidget ? "full-width" : "";

  useEffect(() => {
    storyHeaderInViewRef.current = storyHeaderInView;
  }, [storyHeaderInView]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleActiveStoryChange = (event) => {
      const activeStoryContentId =
        get(event, ["detail", "story", "story-content-id"], "") || get(event, ["detail", "story", "id"], "");

      if (activeStoryContentId) {
        setIsActivePerpetualStory(activeStoryContentId === storyContentId);
      }
    };

    window.addEventListener("perpetual-story-active", handleActiveStoryChange);

    return () => {
      window.removeEventListener("perpetual-story-active", handleActiveStoryChange);
    };
  }, [storyContentId]);

  const handleScroll = () => {
    if (!storyHeaderRef.current) {
      return;
    }

    const storyHolders = Array.from(document.querySelectorAll(STORY_HOLDER_SELECTOR));
    let activeStoryHolder = storyHeaderRef.current.closest(STORY_HOLDER_SELECTOR);

    if (storyHolders.length) {
      activeStoryHolder = storyHolders.find((storyHolder) => {
        const storyHolderPosition = storyHolder.getBoundingClientRect();

        return (
          storyHolderPosition.top <= STORY_HEADER_SWITCH_OFFSET &&
          storyHolderPosition.bottom > STORY_HEADER_SWITCH_OFFSET
        );
      });

      activeStoryHolder =
        activeStoryHolder ||
        storyHolders.find((storyHolder) => storyHolder.getBoundingClientRect().top > STORY_HEADER_SWITCH_OFFSET) ||
        storyHolders[storyHolders.length - 1];
    }

    const activeStoryHeader = activeStoryHolder?.querySelector(STORY_HEADER_SELECTOR) || storyHeaderRef.current;
    const storyHeaderPosition = activeStoryHeader.getBoundingClientRect();
    const isUpcomingStoryHeaderVisible = storyHolders.some((storyHolder) => {
      const storyHeader = storyHolder.querySelector(STORY_HEADER_SELECTOR);

      if (!storyHeader || storyHeader === activeStoryHeader) {
        return false;
      }

      const storyHeaderPosition = storyHeader.getBoundingClientRect();

      return storyHeaderPosition.top > STORY_HEADER_SWITCH_OFFSET && storyHeaderPosition.top < window.innerHeight;
    });
    const isStoryHeaderInView = storyHeaderPosition.bottom > STORY_HEADER_SWITCH_OFFSET || isUpcomingStoryHeaderVisible;

    if (!isStoryHeaderInView && storyHeaderInViewRef.current) {
      storyHeaderInViewRef.current = false;
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: false,
      });
    } else if (isStoryHeaderInView && !storyHeaderInViewRef.current) {
      storyHeaderInViewRef.current = true;
      dispatch({
        type: STORY_HEADER_IN_VIEW,
        payload: true,
      });
    }
  };

  const trackStoryInteraction = (eventAction, callback) => {
    const pageType = "story-page";
    const { category, subCategory, contentId } = getCategoryAndSubCategory(window.location, pageType, qtConfig, story);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "dl_story_interactions",
      event_action: eventAction,
      category,
      sub_category: subCategory,
      content_id: contentId,
      template_name: getTemplateName(story?.["story-template"] || pageType),
      platform: window.innerWidth >= 768 ? "desktop" : "mobile",
    });
    if (typeof callback === "function") {
      callback();
    }
  };

  const handleHighlightsClick = () => {
    trackStoryInteraction("highlight_clicks", () => setShowHighlights(true));
  };

  const handleSummaryClick = () => {
    if (!isSummaryButtonEnabled) {
      return;
    }
    trackStoryInteraction("summary_click", () => setShowSummary(true));
  };

  const handleAiHubClick = () => {
    setAiHubScrollTarget(null);
    setAiHubClick(true);
  };

  const throttledHandleScroll = throttle(handleScroll, 200);
  useEffect(() => {
    window.addEventListener("scroll", throttledHandleScroll);
    return () => {
      window.removeEventListener("scroll", throttledHandleScroll);
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  const scrollY = typeof window !== "undefined" ? window.scrollY : 0;

  useEffect(() => {
    if (showSummary || aiHubClick) {
      document.body.classList.add("no-scroll-forSummary");
      document.body.classList.remove("scroll-forSummar");
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
    } else {
      document.body.classList.remove("no-scroll-forSummary");
      document.body.classList.add("scroll-forSummar");
    }
    return () => {
      const scrollY = Math.abs(parseInt(document.body.style.top || "0", 10));
      document.body.classList.remove("no-scroll-forSummary");
      document.body.classList.add("scroll-forSummar");
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo(0, scrollY);
    };
  }, [showSummary, aiHubClick]);

  useEffect(() => {
    if (!shouldEnableAiHubForTextStory || typeof window === "undefined") {
      return undefined;
    }

    const handleOpenAiHub = (event) => {
      const eventStoryContentId = event?.detail?.storyContentId;
      const fallbackStoryContentId = event?.detail?.fallbackStoryContentId;
      const matchingStoryIds = [storyId, storyContentId].filter(Boolean);

      if (!matchingStoryIds.includes(eventStoryContentId) && !matchingStoryIds.includes(fallbackStoryContentId)) {
        return;
      }

      const eventTarget = event?.detail?.target;
      const scrollTarget =
        eventTarget === FIRST_PRINCIPLES_TARGET || eventTarget === TIMELINE_TARGET ? eventTarget : null;

      setAiHubScrollTarget(scrollTarget);
      setAiHubClick(true);
    };

    window.addEventListener(OPEN_AI_HUB_EVENT, handleOpenAiHub);

    return () => {
      window.removeEventListener(OPEN_AI_HUB_EVENT, handleOpenAiHub);
    };
  }, [shouldEnableAiHubForTextStory, storyContentId, storyId]);

  useEffect(() => {
    if (!shouldEnableAiHubForTextStory || !isMobile || !isActivePerpetualStory || isContinueReadingPreviewActive) {
      setShowAiHubPanel(false);
      return undefined;
    }

    let attachObserverTimeout = null;
    let isMounted = true;
    let firstParagraph = null;

    const getStoryHolder = () => storyHeaderRef.current?.closest(STORY_HOLDER_SELECTOR) || document;
    const getFirstStoryParagraph = () => {
      const storyHolder = getStoryHolder();

      return (
        storyHolder.querySelector(".story-elements .arr--text-element p") ||
        storyHolder.querySelector(".story-elements [data-test-id='text'] p") ||
        storyHolder.querySelector(".text-container .arr--text-element p")
      );
    };

    const updatePanelVisibility = () => {
      if (!firstParagraph || !isMounted) {
        return;
      }

      const triggerPoint = window.innerHeight * AI_HUB_STICKY_PARAGRAPH_TRIGGER_RATIO;
      setShowAiHubPanel(firstParagraph.getBoundingClientRect().top <= triggerPoint);
    };

    const handlePanelPositionChange = throttle(updatePanelVisibility, 100);

    const attachObservers = () => {
      firstParagraph = getFirstStoryParagraph();
      if (!firstParagraph) {
        attachObserverTimeout = window.setTimeout(attachObservers, 100);
        return;
      }

      updatePanelVisibility();
    };

    window.addEventListener("scroll", handlePanelPositionChange, { passive: true });
    window.addEventListener("resize", handlePanelPositionChange);
    attachObservers();

    return () => {
      isMounted = false;
      window.removeEventListener("scroll", handlePanelPositionChange);
      window.removeEventListener("resize", handlePanelPositionChange);
      handlePanelPositionChange.cancel();
      if (attachObserverTimeout) {
        window.clearTimeout(attachObserverTimeout);
      }
    };
  }, [shouldEnableAiHubForTextStory, isActivePerpetualStory, isContinueReadingPreviewActive]);

  const iconSize = { width: "18px", height: "18px" };

  if (!headline) return null;
  const isMagazineType = storyTemplate === "magazine";

  const trackPopupSocialShareClick = (platform) => {
    if (typeof window !== "undefined" && window.dataLayer) {
      const pageType = "story-page";
      const { category, subCategory, contentId } = getCategoryAndSubCategory(
        window.location,
        pageType,
        qtConfig,
        story
      );

      window.dataLayer.push({
        event: "dl_story_interactions",
        event_action: "share_click",
        link_text: platform || "share",
        category,
        sub_category: subCategory,
        content_id: contentId,
        template_name: getTemplateName(story?.["story-template"] || pageType),
        platform: window.innerWidth >= 768 ? "desktop" : "mobile",
      });
    }
  };
  const isAuthorImageRequired = get(story, ["metadata", "story-attributes", "authorimagerequired", "0"], false);
  const SocialShareComponent = () => {
    return (
      <div id={`story-share-${story.id}`} className="content-style">
        <SocialShare
          template={(props) => (
            <SocialShareTemplate
              {...props}
              story={story}
              qtConfig={qtState.config}
              onShareClick={(platform) => trackPopupSocialShareClick(platform?.toLowerCase())}
            />
          )}
          fullUrl={encodeURI(story.url)}
          title={story.headline}
          shareIconId={`share-icon-story-share-${story.id}`}
          buttonLabel={"Share"}
        />
      </div>
    );
  };

  const isMagazineStoryWrapper = isMagazineType && Boolean(magazineDetails);
  let adConfig = get(qtState, ["config", "ads-config", "ad_slots", qtState.pageType, "right_side"], {});
  if (qtState.pageType === "story-page" && qtState?.data?.story?.["story-template"] !== "text") {
    adConfig = get(
      qtState,
      ["config", "ads-config", "ad_slots", qtState?.data?.story?.["story-template"], "right_side"],
      {}
    );
  }

  const bookReviewAndAdLogic = () => {
    if (isBookReview && showBookReview && isValidBookReview) {
      return <BookReviewCard reviewInfo={story.metadata} />;
    } else if (isMagazineStoryWrapper) {
      return <MagazineStoryHeaderCard story={story} magazineDetails={magazineDetails} />;
    } else if (!isAdfreePage && showAd && !isMagazineType) {
      return (
        <div styleName="right-section">
          <DfpComponent
            adStyleName="ad-style-300x250"
            id={`story-page-right-mrec-${storyAdKey}`}
            path={`${networkId}/${adConfig}`}
            adSlotConfigName="mRec_ad"
            showText={true}
            targetStory={story}
          />
        </div>
      );
    } else {
      return null;
    }
  };

  let magazineImagePresent = false;
  magazineImagePresent = magazineDetails?.metadata?.["cover-image"]?.["cover-image-url"] || false;
  const bookStyleName = `${isMagazineStoryWrapper ? "" : "no-magazine-issue"} ${
    isBookReview ? "book-review-button-wrapper" : ""
  } ${isMovieReview ? "review-button-wrapper" : ""} ${
    isMagazineStoryWrapper && magazineImagePresent ? "magazone-image-present" : ""
  }`;

  return (
    <React.Fragment>
      {shouldEnableAiHubForTextStory && (
        <AIHub
          aiHubClick={aiHubClick}
          setAiHubClick={setAiHubClick}
          aiHubScrollTarget={aiHubScrollTarget}
          headline={apiHeadline || headline}
          publishTime={publishTime}
          heroImageS3Key={apiHeroImageS3Key || story["hero-image-s3-key"]}
          summary={summary}
          topic={topic}
          keyTakeaways={keyTakeaways}
          timeline={timeline}
          faqs={faqs}
          story={story}
          glossary={glossary}
          isLoading={isLoading}
          hasSummary={hasSummary}
          hasTopic={hasTopic}
          hasKeyTakeaways={hasKeyTakeaways}
          hasTimeline={hasTimeline}
          hasFaqs={hasFaqs}
          hasGlossary={hasGlossary}
          tweets={tweets}
          hasTweets={hasTweets}
          qtConfig={qtConfig}
          hasInTheStory={hasInTheStory}
          inTheStory={inTheStory}
          shouldShowAiHubButton={shouldShowAiHubButton}
          storyInstanceId={storyInstanceId}
        />
      )}
      <div className="container story-header-wrapper" styleName={`story-header-wrapper `} data-story-header>
        {storyTemplate === "text" &&
          shouldShowAiHubButton &&
          isMobile &&
          showAiHubPanel &&
          isActivePerpetualStory &&
          !isContinueReadingPreviewActive && (
            <div styleName="aihub-button">
              <div styleName="notif-dot"></div>
              <div styleName="notif-ring"></div>
              <div styleName="notif-ring"></div>
              <button aria-label="AI Hub" onClick={handleAiHubClick}>
                <div styleName="icon-text">AI Hub</div>
              </button>
            </div>
          )}
        <div styleName={`top-content`} ref={storyHeaderRef}>
          <div styleName={`left-section ${fullWidth} ${isMagazineType ? "magazine-template" : ""} ${bookStyleName}`}>
            {/* <div> */}
            {!hideBreadcrumb && !isSponsoredStory && !isBookReview && !isMovieReview ? (
              <div styleName="breadcrumb-story" style={{ textAlign: isAdfreePage && !isMagazine ? "center" : "left" }}>
                <BreadCrumb type="story-page" story={story} />
              </div>
            ) : null}
            <LiveButton story={story} storyHeader={true} />
            {isBookReview && <BookReviewButton />}
            {isMovieReview && <ReviewButton />}
            {/* </div> */}

            {isSponsoredStory ? (
              <div styleName="sponsored-logo">
                <FortuneExchange />
                <div styleName="sponsored-tag">{STATIC_KEYWORDS.SPONSORED}</div>
              </div>
            ) : null}

            <Headline
              type={1}
              customStyleName={`h1-style ${isMagazineType ? "magazine" : ""}`}
              customClassName="article-header-headline"
            >
              {headline}
            </Headline>
            <div styleName="content-wrapper">
              {isAuthorImageRequired &&
                isAuthorImageRequired !== "false" &&
                story["story-template"] !== "sponsored" && <StoryAuthorImages authors={storyAuthorData} />}
              <div styleName="content">
                <StoryAuthors
                  storyAuthor={storyAuthorData}
                  customStyleName={"authors-name"}
                  story={story}
                  config={qtState.config}
                  pageType="story-page"
                />
                <div styleName="date-and-time">
                  Last Updated:
                  <PublishDateAndTime publishTime={publishTime} />
                  {/* {readTime ? (
                    <>
                      <span>/</span>
                      <ReadTime time={readTime} />
                    </>
                  ) : (
                    ""
                  )} */}
                </div>
              </div>
              <div styleName={`${isTextStory && shouldEnableAiHubForTextStory ? "summarybtn" : ""} btns-wrapper`}>
                {isLiveBlog ? (
                  <Button
                    iconType="highlights"
                    iconHeight={iconSize.height}
                    iconWidth={iconSize.width}
                    customStyleName="story-header-btn"
                    onClickFunction={handleHighlightsClick}
                  >
                    Highlights
                  </Button>
                ) : (
                  <>
                    {shouldRenderSummaryButton && (
                      <Button
                        iconType="summary"
                        iconHeight={iconSize.height}
                        iconWidth={iconSize.width}
                        customStyleName="story-header-btn"
                        onClickFunction={handleSummaryClick}
                        ariaLabel={isSummaryButtonPending ? "Summary is loading" : "Open Summary"}
                        disabled={!isSummaryButtonEnabled}
                      >
                        Summarise
                      </Button>
                    )}
                    {shouldEnableAiHubForTextStory && (
                      <Button
                        iconType=""
                        iconHeight={iconSize.height}
                        iconWidth={iconSize.width}
                        customStyleName="story-header-btn"
                        onClickFunction={handleAiHubClick}
                        ariaLabel={shouldShowAiHubButton ? "Open AI Hub" : "AI Hub is loading"}
                        disabled={!shouldShowAiHubButton}
                      >
                        AI Hub
                      </Button>
                    )}
                  </>
                )}
                <SocialShareComponent />
              </div>
            </div>
          </div>
          {isDesktop && <>{bookReviewAndAdLogic()}</>}
        </div>
        {story?.subheadline && (
          <TextWithReadMore
            textElement={{ text: story?.subheadline }}
            deviceType={deviceType}
            isMagazine={isMagazineType}
            storyHeader={true}
          />
        )}
        {showReview && isMovieReview && <MoviewReviewCard reviewInfo={story.metadata} />}
        {showHeroImage && story["hero-image-s3-key"] ? (
          <div styleName={`hero-image-wrapper ${story?.subheadline !== null ? "" : "no-subHeadline-wrapper"}`}>
            {/* {!isAdfreePage && isMagazineStoryWrapper && (
              <DfpComponent
                adStyleName="ad-slot-size-300x250"
                id={`magazine-story-page-right-mrec-${storyAdKey}`}
                path={`${networkId}/RPSG_Opn_Mob/RPSG_Opn_AS_mREC`}
                adSlotConfigName="mRec_ad"
                showText={true}
                targetStory={story}
              />
            )} */}
            <HeroImage
              story={story}
              aspectRatio={[[16, 9]]}
              isStoryPageImage={true}
              showIcon={false}
              isStoryHeader={true}
            />
          </div>
        ) : null}
        {showHeroImage ? <CaptionAttribution story={story} /> : null}
        {isMobile && isMagazineStoryWrapper && (
          <>
            <br />
            <MagazineStoryHeaderCard story={story} magazineDetails={magazineDetails} isMagazineRemove />
          </>
        )}
      </div>
      {shouldShowSummary && showSummary && (
        <SummaryCard
          setShowSummary={setShowSummary}
          story={story}
          summaryIndex={summaryIndex}
          summaryHtml={shouldUseApiSummary ? summary : ""}
          summaryTitle={shouldUseApiSummary ? apiHeadline : ""}
          summaryImage={shouldUseApiSummary ? apiHeroImageS3Key : ""}
        />
      )}
      {showSummary && <div styleName="blur-bg" />}
    </React.Fragment>
  );
};

StoryHeader.defaultProps = {
  showAd: true,
  headline: "",
  storyAuthorData: [],
  publishTime: 0,
  readTime: 0,
  showHeroImage: true,
  showReview: false,
  showBookReview: false,
  isValidBookReview: false,
  hideBreadcrumb: false,
  isContinueReadingPreviewActive: false,
  isPerpetualStory: false,
  storyInstanceId: "",
};

StoryHeader.propTypes = {
  showAd: bool,
  showHeroImage: bool,
  headline: string.isRequired,
  storyAuthorData: array.isRequired,
  publishTime: number,
  readTime: number,
  story: object,
  magazineDetails: object,
  setShowHighlights: func,
  showReview: bool,
  showBookReview: bool,
  isValidBookReview: bool,
  hideBreadcrumb: bool,
  isContinueReadingPreviewActive: bool,
  isPerpetualStory: bool,
  storyInstanceId: string,
};

export default StoryHeader;
